import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Settings2 } from 'lucide-react';
import { ErrorState } from '@/components/shared/page';
import { ObjectIndex, FilterPill, AddButton } from '@/components/shared/object-index';
import { useT } from '@/lib/i18n';
import { Can } from '@/components/shared/can';
import { Badge } from '@/components/ui/badge';
import { Avatar, Skeleton } from '@/components/ui/misc';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useBoard, useMoveDeal, usePipelines } from './api';
import { DEAL_TYPE_LABELS } from './labels';
import { DealDialog } from './deal-dialog';
import { MoveDealDialog } from './move-deal-dialog';
import type { BoardColumn, Deal } from './types';

/**
 * The pipeline Kanban (spec PIPE-03). Cards are dragged between columns with
 * the HTML drag-and-drop API — no library, and it keeps keyboard users on the
 * card menu, which does the same thing.
 */
export function DealsBoardPage() {
  const t = useT();
  const { data: pipelines = [] } = usePipelines();
  const [pipelineId, setPipelineId] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [dragging, setDragging] = useState<Deal | null>(null);
  const [pendingMove, setPendingMove] = useState<{ deal: Deal; column: BoardColumn } | null>(null);

  const { data, isLoading, error, refetch } = useBoard({
    pipelineId: pipelineId || undefined,
    ownerId: ownerFilter || undefined,
  });
  const move = useMoveDeal();

  const currency = data?.columns[0]?.deals[0]?.currency ?? 'AED';

  const onDrop = (column: BoardColumn) => {
    if (!dragging || dragging.stageId === column.stage.id) {
      setDragging(null);
      return;
    }

    // Won and lost need extra input, and a stage may demand fields first — both
    // go through the dialog rather than failing silently on the board.
    const needsDialog =
      column.stage.stageType !== 'OPEN' ||
      missingFields(dragging, findStage(data?.columns, dragging.stageId)).length > 0;

    if (needsDialog) {
      setPendingMove({ deal: dragging, column });
    } else {
      move.mutate({ id: dragging.id, stageId: column.stage.id });
    }
    setDragging(null);
  };

  return (
    <div>
      <ObjectIndex
        title="Deals"
        count={data?.columns.reduce((sum, column) => sum + column.count, 0)}
        mode="board"
        tableHref="/crm/deals/list"
        boardHref="/crm/deals"
        calendarHref="/crm/deals/calendar"
        filters={
          <>
            <FilterPill
              label="Pipeline"
              value={pipelineId}
              onChange={setPipelineId}
              options={pipelines.map((pipeline) => ({
                value: pipeline.id,
                label: `${pipeline.name}${pipeline.isDefault ? ` (${t('default')})` : ''}`,
              }))}
            />
            <FilterPill
              label="Deal owner"
              value={ownerFilter}
              onChange={setOwnerFilter}
              options={ownerOptions(data?.columns).map((owner) => ({ value: owner.id, label: owner.name }))}
            />
          </>
        }
        hasFilters={Boolean(pipelineId || ownerFilter)}
        onClearFilters={() => {
          setPipelineId('');
          setOwnerFilter('');
        }}
        toolbarEnd={
          <>
            {data && (
              <span className="hidden text-sm text-muted-foreground lg:inline">
                <span className="tabular font-semibold text-foreground">
                  {formatMoney(sumColumns(data.columns, 'totalValue'), currency)}
                </span>{' '}
                {t('total')} ·{' '}
                <span className="tabular font-semibold text-foreground">
                  {formatMoney(sumColumns(data.columns, 'weightedValue'), currency)}
                </span>{' '}
                {t('weighted')}
              </span>
            )}
            <Can permission="crm.pipeline.create">
              <Link
                to="/crm/pipelines/deal"
                title={t('Edit pipeline stages')}
                className="flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Settings2 className="h-4 w-4" />
              </Link>
            </Can>
          </>
        }
        actions={
          <Can permission="crm.deal.create">
            <AddButton onClick={() => setCreating(true)} label="Add deal" />
          </Can>
        }
      >
        {error ? (
          <div className="p-4">
            <ErrorState message="Could not load the pipeline." onRetry={() => void refetch()} />
          </div>
        ) : isLoading ? (
          <div className="flex gap-3 overflow-x-auto p-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="h-96 w-[calc(100%-2.5rem)] shrink-0 md:w-[17rem]" />
            ))}
          </div>
        ) : (
          <div className="scrollbar-thin flex snap-x snap-mandatory gap-3 overflow-x-auto bg-muted/40 p-3 md:snap-none">
            {data?.columns.map((column) => (
              <div
                key={column.stage.id}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => onDrop(column)}
                className={cn(
                  'flex max-h-[calc(100dvh-17rem)] min-h-[24rem] w-[calc(100%-2.5rem)] shrink-0 snap-start flex-col rounded-lg bg-muted/70 transition-colors md:w-[17rem]',
                  dragging && dragging.stageId !== column.stage.id && 'bg-primary/[0.07] ring-2 ring-primary/30',
                )}
              >
                <div className="px-2.5 pb-2 pt-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className="truncate rounded px-2 py-0.5 text-xs font-bold text-white"
                      style={{ backgroundColor: column.stage.colour }}
                    >
                      {column.stage.name}
                    </span>
                    <span className="tabular text-xs font-semibold text-muted-foreground">{column.count}</span>
                  </div>
                </div>

                <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto px-2 pb-2">
                  {column.deals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      currency={currency}
                      onDragStart={() => setDragging(deal)}
                      onDragEnd={() => setDragging(null)}
                    />
                  ))}
                  {column.deals.length === 0 && (
                    <p className="rounded-md border border-dashed px-2 py-6 text-center text-xs text-muted-foreground">
                      {t('Drag deals here')}
                    </p>
                  )}
                </div>

                <div className="border-t border-border/70 px-3 py-2 text-xs text-muted-foreground">
                  <span className="tabular font-semibold text-foreground">{formatMoney(column.totalValue, currency)}</span>{' '}
                  {t('total')}
                  {column.stage.stageType === 'OPEN' && (
                    <span className="block">
                      <span className="tabular">{formatMoney(column.weightedValue, currency)}</span> {t('weighted')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ObjectIndex>

      {creating && (
        <DealDialog pipelineId={pipelineId || undefined} onClose={() => setCreating(false)} />
      )}
      {pendingMove && (
        <MoveDealDialog
          deal={pendingMove.deal}
          targetStage={pendingMove.column.stage}
          currentStage={findStage(data?.columns, pendingMove.deal.stageId)}
          onClose={() => setPendingMove(null)}
        />
      )}
    </div>
  );
}

function DealCard({
  deal,
  currency,
  onDragStart,
  onDragEnd,
}: {
  deal: Deal;
  currency: string;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  return (
    <Link
      to={`/crm/deals/${deal.id}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="group block cursor-grab rounded-md border bg-card p-3 shadow-card transition-all hover:border-foreground/20 hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-link line-clamp-2 text-sm font-semibold group-hover:underline">{deal.title}</span>
        {deal.isRotting && (
          <span title="No activity for a while">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          </span>
        )}
      </div>

      <dl className="mt-2 space-y-1 text-xs">
        <div className="flex gap-1.5">
          <dt className="text-muted-foreground">Amount:</dt>
          <dd className="tabular font-semibold">{formatMoney(deal.value, currency)}</dd>
        </div>
        <div className="flex gap-1.5">
          <dt className="text-muted-foreground">Company:</dt>
          <dd className="truncate">{deal.company.name}</dd>
        </div>
      </dl>

      <div className="mt-2.5 flex items-center justify-between gap-2 border-t pt-2">
        <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <Avatar name={deal.owner?.name ?? '?'} size={18} />
          <span className="truncate">{deal.owner?.name ?? 'No owner'}</span>
        </span>
        <Badge variant="secondary" className="shrink-0 text-[10px]">
          {DEAL_TYPE_LABELS[deal.dealType]}
        </Badge>
      </div>
    </Link>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function findStage(columns: BoardColumn[] | undefined, stageId: string) {
  return columns?.find((column) => column.stage.id === stageId)?.stage;
}

/** Which of the current stage's required fields the deal has not filled in. */
export function missingFields(
  deal: Deal,
  stage: { requiredFields: string[] } | undefined,
): string[] {
  if (!stage) return [];
  return stage.requiredFields.filter((field) => {
    switch (field) {
      case 'value':
        return !deal.value || deal.value === '0.00';
      case 'expectedCloseDate':
        return !deal.expectedCloseDate;
      case 'contactId':
        return !deal.contactId;
      case 'ownerId':
        return !deal.ownerId;
      case 'probability':
        return deal.probability === null;
      case 'source':
        return !deal.source;
      default:
        return false;
    }
  });
}

function sumColumns(columns: BoardColumn[], key: 'totalValue' | 'weightedValue'): string {
  // Values are decimal strings; sum in minor units so nothing is lost.
  const total = columns.reduce((sum, column) => {
    const value = column[key] ?? '0';
    const [whole = '0', fraction = '00'] = value.replace('-', '').split('.');
    const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2));
    return sum + (value.startsWith('-') ? -minor : minor);
  }, 0n);

  const negative = total < 0n;
  const abs = negative ? -total : total;
  return `${negative ? '-' : ''}${abs / 100n}.${(abs % 100n).toString().padStart(2, '0')}`;
}

function ownerOptions(columns: BoardColumn[] | undefined) {
  const owners = new Map<string, { id: string; name: string }>();
  for (const column of columns ?? []) {
    for (const deal of column.deals) {
      if (deal.owner) owners.set(deal.owner.id, { id: deal.owner.id, name: deal.owner.name });
    }
  }
  return [...owners.values()].sort((a, b) => a.name.localeCompare(b.name));
}
